/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { MessagesSubscriptionsNewOrganisation } from '../models/MessagesSubscriptionsNewOrganisation';
import type { MessagesSubscriptionsNewOrganisationTeam } from '../models/MessagesSubscriptionsNewOrganisationTeam';
import type { MessagesSubscriptionsNewOrganisationUser } from '../models/MessagesSubscriptionsNewOrganisationUser';
import type { MessagesSubscriptionsNewOrganisationWorkspace } from '../models/MessagesSubscriptionsNewOrganisationWorkspace';
import type { MessagesSubscriptionsOrganisation } from '../models/MessagesSubscriptionsOrganisation';
import type { MessagesSubscriptionsOrganisationSummary } from '../models/MessagesSubscriptionsOrganisationSummary';
import type { MessagesSubscriptionsOrganisationTeam } from '../models/MessagesSubscriptionsOrganisationTeam';
import type { MessagesSubscriptionsOrganisationTeamRole } from '../models/MessagesSubscriptionsOrganisationTeamRole';
import type { MessagesSubscriptionsOrganisationTeamUser } from '../models/MessagesSubscriptionsOrganisationTeamUser';
import type { MessagesSubscriptionsOrganisationUser } from '../models/MessagesSubscriptionsOrganisationUser';
import type { MessagesSubscriptionsOrganisationWorkspace } from '../models/MessagesSubscriptionsOrganisationWorkspace';
import type { MessagesSubscriptionsOrganisationWorkspaceSummary } from '../models/MessagesSubscriptionsOrganisationWorkspaceSummary';
import type { MessagesSubscriptionsUpdateOrganisationTeam } from '../models/MessagesSubscriptionsUpdateOrganisationTeam';
import type { MessagesSubscriptionsUpdateOrganisationUser } from '../models/MessagesSubscriptionsUpdateOrganisationUser';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class SubscriptionsApiService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     *  (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser)
     * @param orgId
     * @returns MessagesSubscriptionsOrganisationSummary OK
     * @throws ApiError
     */
    public getSubscriptionsApiGetOrganisation(
        orgId?: number,
    ): CancelablePromise<MessagesSubscriptionsOrganisationSummary> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/subscriptions/organisation',
            query: {
                'orgId': orgId,
            },
        });
    }

    /**
     * Adds a new organisation. (Authorised Roles: SuperUser)
     * @param requestBody The organisation to add.
     * @returns MessagesSubscriptionsOrganisationSummary OK
     * @throws ApiError
     */
    public putSubscriptionsApiAddOrganisation(
        requestBody?: MessagesSubscriptionsNewOrganisation,
    ): CancelablePromise<Array<MessagesSubscriptionsOrganisationSummary>> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/rest/subscriptions/organisation',
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     * Updates an existing organisation. (Authorised Roles: PlatformAdministrator, SuperUser)
     * @param requestBody The organisation with updated information.
     * @returns MessagesSubscriptionsOrganisationSummary OK
     * @throws ApiError
     */
    public postSubscriptionsApiUpdateOrganisation(
        requestBody?: MessagesSubscriptionsOrganisation,
    ): CancelablePromise<Array<MessagesSubscriptionsOrganisationSummary>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/rest/subscriptions/organisation',
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     * Retrieves the list of organisations. (Authorised Roles: ProjectMember, ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser)
     * @returns MessagesSubscriptionsOrganisationSummary OK
     * @throws ApiError
     */
    public getSubscriptionsApiGetOrganisations(): CancelablePromise<Array<MessagesSubscriptionsOrganisationSummary>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/subscriptions/organisations',
        });
    }

    /**
     * Freezes an organisation, making it read-only. (Authorised Roles: ProjectAdministrator, PlatformAdministrator, SuperUser)
     * @param requestBody The organisation to freeze.
     * @returns MessagesSubscriptionsOrganisationSummary OK
     * @throws ApiError
     */
    public postSubscriptionsApiFreezeOrganisation(
        requestBody?: MessagesSubscriptionsOrganisation,
    ): CancelablePromise<Array<MessagesSubscriptionsOrganisationSummary>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/rest/subscriptions/organisation/freeze',
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     * Return user (Authorised Roles: ProjectAdministrator, PlatformAdministrator, SuperUser)
     * @param userId The ID of the user.
     * @returns MessagesSubscriptionsOrganisationUser OK
     * @throws ApiError
     */
    public getSubscriptionsApiGetOrganisationUser(
        userId?: number,
    ): CancelablePromise<MessagesSubscriptionsOrganisationUser> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/subscriptions/organisation/user',
            query: {
                'userId': userId,
            },
        });
    }

    /**
     * Lists all users in an organisation. (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser)
     * @param orgId The ID of the organisation.
     * @returns MessagesSubscriptionsOrganisationUser OK
     * @throws ApiError
     */
    public getSubscriptionsApiListOrganisationUsers(
        orgId?: number,
    ): CancelablePromise<Array<MessagesSubscriptionsOrganisationUser>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/subscriptions/organisation/users',
            query: {
                'orgId': orgId,
            },
        });
    }

    /**
     * Adds a new user to an organisation. (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser)
     * @param requestBody The new organisation user to add.
     * @returns MessagesSubscriptionsOrganisationUser OK
     * @throws ApiError
     */
    public putSubscriptionsApiAddUser(
        requestBody?: MessagesSubscriptionsNewOrganisationUser,
    ): CancelablePromise<Array<MessagesSubscriptionsOrganisationUser>> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/rest/subscriptions/organisation/users',
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     * Updates an existing user within an organisation. (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser)
     * @param requestBody The user with updated information.
     * @returns MessagesSubscriptionsOrganisationUser OK
     * @throws ApiError
     */
    public postSubscriptionsApiUpdateUser(
        requestBody?: MessagesSubscriptionsUpdateOrganisationUser,
    ): CancelablePromise<Array<MessagesSubscriptionsOrganisationUser>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/rest/subscriptions/organisation/users',
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     * Deletes a user from an organisation. (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser)
     * @param userId The ID of the user to delete.
     * @returns MessagesSubscriptionsOrganisationUser OK
     * @throws ApiError
     */
    public deleteSubscriptionsApiDeleteUser(
        userId?: number,
    ): CancelablePromise<Array<MessagesSubscriptionsOrganisationUser>> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/rest/subscriptions/organisation/users',
            query: {
                'userId': userId,
            },
        });
    }

    /**
     * Lists platform admins assigned to an organisation (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser)
     * @param organisationId
     * @returns MessagesSubscriptionsOrganisationUser OK
     * @throws ApiError
     */
    public getSubscriptionsApiListOrganisationPlatformAdmins(
        organisationId?: number,
    ): CancelablePromise<Array<MessagesSubscriptionsOrganisationUser>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/subscriptions/organisations/admins',
            query: {
                'organisationId': organisationId,
            },
        });
    }

    /**
     * Assign a platform admin user to an organisation (Authorised Roles: SuperUser)
     * @param organisationId
     * @param adminId
     * @returns MessagesSubscriptionsOrganisationUser OK
     * @throws ApiError
     */
    public putSubscriptionsApiAddPlatformAdmin(
        organisationId?: number,
        adminId?: number,
    ): CancelablePromise<MessagesSubscriptionsOrganisationUser> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/rest/subscriptions/organisations/admins',
            query: {
                'organisationId': organisationId,
                'adminId': adminId,
            },
        });
    }

    /**
     * Unassign at platform admin from an organisation (Authorised Roles: SuperUser)
     * @param organisationId
     * @param adminId
     * @returns MessagesSubscriptionsOrganisationUser OK
     * @throws ApiError
     */
    public deleteSubscriptionsApiRemovePlatformAdmin(
        organisationId?: number,
        adminId?: number,
    ): CancelablePromise<MessagesSubscriptionsOrganisationUser> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/rest/subscriptions/organisations/admins',
            query: {
                'organisationId': organisationId,
                'adminId': adminId,
            },
        });
    }

    /**
     * Lists all available platform admins (based on role) (Authorised Roles: SuperUser)
     * @returns MessagesSubscriptionsOrganisationUser OK
     * @throws ApiError
     */
    public getSubscriptionsApiListAllPlatformAdmins(): CancelablePromise<Array<MessagesSubscriptionsOrganisationUser>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/subscriptions/organisations/admins/all',
        });
    }

    /**
     * Freezes a user, making their account read-only. (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser)
     * @param userId The ID of the user to freeze.
     * @returns MessagesSubscriptionsOrganisationUser OK
     * @throws ApiError
     */
    public postSubscriptionsApiFreezeUser(
        userId?: number,
    ): CancelablePromise<Array<MessagesSubscriptionsOrganisationUser>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/rest/subscriptions/organisation/users/freeze',
            query: {
                'userId': userId,
            },
        });
    }

    /**
     * Unfreezes a user, making their account active (Authorised Roles: PlatformAdministrator, SuperUser)
     * @param userId The ID of the user to unfreeze.
     * @returns MessagesSubscriptionsOrganisationUser OK
     * @throws ApiError
     */
    public postSubscriptionsApiUnfreezeUser(
        userId?: number,
    ): CancelablePromise<Array<MessagesSubscriptionsOrganisationUser>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/rest/subscriptions/organisation/users/unfreeze',
            query: {
                'userId': userId,
            },
        });
    }

    /**
     * Retrieves data for a specified project team (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser)
     * @param teamId The ID of the specified project team.
     * @returns MessagesSubscriptionsOrganisationTeam OK
     * @throws ApiError
     */
    public getSubscriptionsApiGetOrganisationProjectTeam(
        teamId: number,
    ): CancelablePromise<MessagesSubscriptionsOrganisationTeam> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/subscriptions/organisations/teams/{teamId}',
            path: {
                'teamId': teamId,
            },
        });
    }

    /**
     * Retrieves the list of organisation project teams. (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser)
     * @param orgId The ID of the organisation.
     * @returns MessagesSubscriptionsOrganisationTeam OK
     * @throws ApiError
     */
    public getSubscriptionsApiGetOrganisationTeams(
        orgId: number,
    ): CancelablePromise<Array<MessagesSubscriptionsOrganisationTeam>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/subscriptions/organisations/teams',
            query: {
                'orgId': orgId,
            },
        });
    }

    /**
     * Adds a new project team to the organisation. (Authorised Roles: ProjectLeader, PlatformAdministrator, SuperUser)
     * @param requestBody The team to add.
     * @returns MessagesSubscriptionsOrganisationTeam OK
     * @throws ApiError
     */
    public putSubscriptionsApiAddTeam(
        requestBody?: MessagesSubscriptionsNewOrganisationTeam,
    ): CancelablePromise<Array<MessagesSubscriptionsOrganisationTeam>> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/rest/subscriptions/organisation/teams',
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     * Updates a project team (Authorised Roles: ProjectLeader, PlatformAdministrator, SuperUser)
     * @param requestBody
     * @returns MessagesSubscriptionsOrganisationTeam OK
     * @throws ApiError
     */
    public postSubscriptionsApiUpdateTeam(
        requestBody?: MessagesSubscriptionsUpdateOrganisationTeam,
    ): CancelablePromise<Array<MessagesSubscriptionsOrganisationTeam>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/rest/subscriptions/organisation/teams',
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     * Freezes a team (Authorised Roles: ProjectLeader, PlatformAdministrator, SuperUser)
     * @param teamId
     * @returns MessagesSubscriptionsOrganisationTeam OK
     * @throws ApiError
     */
    public postSubscriptionsApiFreezeTeam(
        teamId?: number,
    ): CancelablePromise<Array<MessagesSubscriptionsOrganisationTeam>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/rest/subscriptions/organisation/teams/freeze',
            query: {
                'teamId': teamId,
            },
        });
    }

    /**
     * Dynamically returns a list of team users based on input parameters (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser)
     * @param orgId
     * @returns MessagesSubscriptionsOrganisationTeamUser OK
     * @throws ApiError
     */
    public getSubscriptionsApiGetProjectTeamUsersForOrg(
        orgId: number,
    ): CancelablePromise<Array<MessagesSubscriptionsOrganisationTeamUser>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/subscriptions/organisation/{orgId}/projects/teams/users',
            path: {
                'orgId': orgId,
            },
            errors: {
                400: `Bad Request`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     *  (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser, ApplicationAdministrator)
     * @param teamId
     * @returns MessagesSubscriptionsOrganisationTeamUser OK
     * @throws ApiError
     */
    public getSubscriptionsApiListTeamUsers(
        teamId?: number,
    ): CancelablePromise<Array<MessagesSubscriptionsOrganisationTeamUser>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/subscriptions/organisation/teams/users',
            query: {
                'teamId': teamId,
            },
        });
    }

    /**
     *  (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser)
     * @param requestBody
     * @returns MessagesSubscriptionsOrganisationTeamUser OK
     * @throws ApiError
     */
    public postSubscriptionsApiAddTeamUser(
        requestBody?: MessagesSubscriptionsOrganisationTeamUser,
    ): CancelablePromise<MessagesSubscriptionsOrganisationTeamUser> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/rest/subscriptions/organisation/teams/users',
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     *  (Authorised Roles: PlatformAdministrator, SuperUser)
     * @param requestBody
     * @returns MessagesSubscriptionsOrganisationTeamUser OK
     * @throws ApiError
     */
    public deleteSubscriptionsApiDeleteTeamUser(
        requestBody?: MessagesSubscriptionsOrganisationTeamUser,
    ): CancelablePromise<MessagesSubscriptionsOrganisationTeamUser> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/rest/subscriptions/organisation/teams/users',
            body: requestBody,
            mediaType: 'application/json-patch+json',
            errors: {
                400: `Bad Request`,
                404: `Not Found`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     *  (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser, ApplicationAdministrator)
     * @param teamId
     * @returns MessagesSubscriptionsOrganisationTeamRole OK
     * @throws ApiError
     */
    public getSubscriptionsApiListTeamRoles(
        teamId?: number,
    ): CancelablePromise<Array<MessagesSubscriptionsOrganisationTeamRole>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/subscriptions/organisation/teams/roles',
            query: {
                'teamId': teamId,
            },
        });
    }

    /**
     *  (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser)
     * @param requestBody
     * @returns MessagesSubscriptionsOrganisationTeamRole OK
     * @throws ApiError
     */
    public putSubscriptionsApiAddTeamRole(
        requestBody?: MessagesSubscriptionsOrganisationTeamRole,
    ): CancelablePromise<MessagesSubscriptionsOrganisationTeamRole> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/rest/subscriptions/organisation/teams/roles',
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     *  (Authorised Roles: PlatformAdministrator, SuperUser)
     * @param requestBody
     * @returns MessagesSubscriptionsOrganisationTeamRole OK
     * @throws ApiError
     */
    public deleteSubscriptionsApiDeleteTeamRole(
        requestBody?: MessagesSubscriptionsOrganisationTeamRole,
    ): CancelablePromise<MessagesSubscriptionsOrganisationTeamRole> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/rest/subscriptions/organisation/teams/roles',
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     *  (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser)
     * @param orgId
     * @returns MessagesSubscriptionsOrganisationWorkspace OK
     * @throws ApiError
     */
    public getSubscriptionsApiGetOrganisationWorkspaces(
        orgId?: number,
    ): CancelablePromise<Array<MessagesSubscriptionsOrganisationWorkspace>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/subscriptions/organisations/workspaces',
            query: {
                'orgId': orgId,
            },
        });
    }

    /**
     *  (Authorised Roles: ProjectLeader, PlatformAdministrator, SuperUser)
     * @param requestBody
     * @returns MessagesSubscriptionsOrganisationWorkspace OK
     * @throws ApiError
     */
    public putSubscriptionsApiAddOrganisationWorkspaces(
        requestBody?: MessagesSubscriptionsNewOrganisationWorkspace,
    ): CancelablePromise<MessagesSubscriptionsOrganisationWorkspace> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/rest/subscriptions/organisations/workspaces',
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     *  (Authorised Roles: ProjectLeader, PlatformAdministrator, SuperUser)
     * @param requestBody
     * @returns MessagesSubscriptionsOrganisationWorkspace OK
     * @throws ApiError
     */
    public deleteSubscriptionsApiDeleteOrganisationWorkspaces(
        requestBody?: MessagesSubscriptionsNewOrganisationWorkspace,
    ): CancelablePromise<MessagesSubscriptionsOrganisationWorkspace> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/rest/subscriptions/organisations/workspaces',
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     *  (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser)
     * @param orgId
     * @param workspaceId
     * @returns MessagesSubscriptionsOrganisationWorkspace OK
     * @throws ApiError
     */
    public getSubscriptionsApiGetOrganisationWorkspace(
        orgId?: number,
        workspaceId?: number,
    ): CancelablePromise<MessagesSubscriptionsOrganisationWorkspace> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/subscriptions/organisations/workspace',
            query: {
                'orgId': orgId,
                'workspaceId': workspaceId,
            },
        });
    }

    /**
     *  (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser)
     * @param orgId
     * @returns MessagesSubscriptionsOrganisationWorkspaceSummary OK
     * @throws ApiError
     */
    public getSubscriptionsApiGetOrganisationWorkspacesSummary(
        orgId?: number,
    ): CancelablePromise<Array<MessagesSubscriptionsOrganisationWorkspaceSummary>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/subscriptions/organisations/workspaces-summary',
            query: {
                'orgId': orgId,
            },
        });
    }

}
